import React from 'react';
import { trackEvent } from '@fff-web/fff-analytics';
import { showErrorToast } from '@fff-web/fff-ui-shared/Toast';
import classNames from 'classnames';
import { useSharedContext } from '../../pages/GTBCustomize/ContextProviders/SharedContext';
import EXPERIENCE_FLOW from '../../utils/constants/experienceFlow';
import styles from './Choice.module.scss';

type Props = {
  title: string;
  image: string;
  handleEditChoice?: (questionId: number) => void;
  itemId?: number;
  soldOut?: boolean;
  isStatic?: boolean;
  isPublicShop?: boolean;
  publicRefill?: boolean;
  brandName?: string | null;
  cadenceType?: string;
  quantity: number;
};

const NINETY_DAYS_AUTOBILL_NOTICE = 'Ships every 90 days';
const WITH_BOX_AUTOBILL_NOTICE = 'First order ships immediately, renewals ship with your Seasonal Box.';
const SOLDOUT_NOTICE = 'This item is sold out. Please remove it.';
const SOLDOUT_TOAST_TITLE = 'Update To Your Cart';
const SOLDOUT_TOAST_CONTENT = 'We’re sorry, some item(s) are no longer available. Please edit your cart and try again.';

const Choice = ({
  title,
  image,
  handleEditChoice,
  itemId,
  soldOut,
  isStatic,
  isPublicShop,
  publicRefill,
  brandName,
  cadenceType,
  quantity,
}: Props) => {
  const {
    planCode,
    campaign,
    timerStatus,
    experienceFlow,
  } = useSharedContext();
  const { campaignId, campaignName } = campaign;
  const isSurprise = title?.toLowerCase().includes('surprise');
  const isGTB = experienceFlow === EXPERIENCE_FLOW.gtb;
  const displayedTitle = publicRefill ? `${title} Subscription` : title;

  const fireEditChoiceSegmentEvent = () => {
    trackEvent({
      ctaName: 'Clicked Element',
      properties: {
        campaign_id: campaignId,
        campaign_name: campaignName,
        customize_experience_type: 'preview_customize',
        cta_name: 'Edit Choice',
        plan_code: planCode,
        timer: timerStatus,
        url: window.location.href,
        path: window.location.pathname,
      },
    });
  };
  const handleClickEdit = () => {
    fireEditChoiceSegmentEvent();
    if (handleEditChoice && itemId) handleEditChoice(itemId);
  };
  if (soldOut) {
    showErrorToast(SOLDOUT_TOAST_TITLE, SOLDOUT_TOAST_CONTENT);
  }

  return (
    <div className={styles.choice} role="listitem">
      <div className={styles.choiceImage}>
        {image && (
          <img
            className={classNames({ [styles.introProductImg]: isPublicShop })}
            src={image}
            alt={title}
            data-testid="choice-image"
          />
        )}
      </div>
      <div className={styles.choiceInfo}>
        {brandName && (
          <div className={styles.choiceInfo__brand}>
            {brandName}
          </div>
        )}
        <div
          className={styles.choiceInfo__title}
          data-testid="choice-title"
        >
          {displayedTitle}
        </div>
        {publicRefill && cadenceType && (
          <span className={styles.choiceInfo__cadence}>
            {cadenceType === 'NINETY_DAYS'
              ? NINETY_DAYS_AUTOBILL_NOTICE
              : WITH_BOX_AUTOBILL_NOTICE}
          </span>
        )}
        {!isSurprise && (
          <span className={styles.choiceInfo__additional}>
            {isStatic ? 'Included' : `x${quantity}`}
          </span>
        )}
        {soldOut && (
          <span className={styles.choiceInfo__lowStock}>
            {SOLDOUT_NOTICE}
          </span>
        )}
        {isGTB && (
          <div className={styles.choiceInfo__editContainer}>
            <span
              className={styles.choiceInfo__edit}
              onClick={handleClickEdit}
              onKeyPress={handleClickEdit}
              role="button"
              tabIndex={0}
            >
              Edit Choice
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

Choice.defaultProps = {
  handleEditChoice: () => { },
  itemId: null,
  soldOut: false,
  isStatic: false,
  isPublicShop: false,
  publicRefill: false,
  brandName: '',
  cadenceType: '',
};

export default Choice;
